// src/components/MeModal.jsx
import React, { useMemo, useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Modal from "./Modal";
import { ensureAt, normalizeHandle, isHandleAvailable, debounce } from "../utils/handle";
import { containsProfanity } from "../utils/profanity";

// Firebase imports - make sure ../firebase exports { auth, db, storage }
import { auth, db, storage } from "../firebase";
import { doc, setDoc, serverTimestamp, getDoc } from "firebase/firestore";
import { ref as storageRef, uploadBytesResumable, getDownloadURL, deleteObject } from "firebase/storage";
import { updateProfile } from "firebase/auth";

/**
 * MeModal - edit displayName + handle + photo + bio
 * - saves photoURL + photoPath + bio to users/{uid}
 * - deletes previous photo at photoPath (if present) after successful update
 * - cache-busts local UI and re-reads Firestore so modal shows new avatar immediately
 */
export default function MeModal({
    open,
    onClose,
    profile = {},
    currentUser = {},
    onSave,
}) {
    // Hooks - unconditional
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [uploadProgress, setUploadProgress] = useState(null); // null or 0-100

    const [form, setForm] = useState({ displayName: "", handle: "", photoURL: "", photoFile: null, bio: "" });

    const [availability, setAvailability] = useState({ checking: false, available: null });
    const latestCheckHandle = useRef(null);

    // initial values derived from props
    const initial = useMemo(() => {
        const photoURL = profile.photoURL || currentUser.photoURL || "";
        const displayName = profile.displayName || currentUser.displayName || "";
        const email = profile.email || currentUser.email || "";
        const handle = profile.handle || (currentUser.email ? currentUser.email.split("@")[0] : "");
        const role = profile.role || "user";
        const uid = profile.uid || currentUser?.uid || profile?.id || null;
        const bio = profile.bio ?? currentUser.bio ?? "";
        return { photoURL, displayName, email, handle, role, uid, bio };
    }, [profile, currentUser]);

    // refs
    const fileInputRef = useRef(null);
    const runCheckRef = useRef(null);

    // create a stable debounced checker (recreates when initial.handle changes)
    useEffect(() => {
        runCheckRef.current = debounce(async (raw) => {
            const normalized = normalizeHandle(raw);
            if (!normalized) {
                setAvailability({ checking: false, available: null });
                return;
            }

            const initNormalized = normalizeHandle(initial.handle || "");
            if (initNormalized && normalized === initNormalized) {
                setAvailability({ checking: false, available: true });
                return;
            }

            latestCheckHandle.current = normalized;
            setAvailability({ checking: true, available: null });

            try {
                const ok = await isHandleAvailable(normalized);
                if (latestCheckHandle.current === normalized) {
                    setAvailability({ checking: false, available: !!ok });
                }
            } catch (e) {
                console.error("handle availability check error", e);
                if (latestCheckHandle.current === normalized) {
                    setAvailability({ checking: false, available: null });
                }
            }
        }, 350);
    }, [initial.handle]);

    // when modal opens, initialize and also fetch latest user doc from Firestore to avoid stale parent state
    useEffect(() => {
        const initForm = async () => {
            if (!open) return;
            setEditing(false);
            setSaving(false);
            setError("");
            setUploadProgress(null);

            // best-effort: read latest user doc from Firestore so modal shows canonical data
            const uid = initial.uid;
            if (uid) {
                try {
                    const userDocRef = doc(db, "users", uid);
                    const snap = await getDoc(userDocRef);
                    if (snap.exists()) {
                        const data = snap.data();
                        setForm({
                            displayName: data.displayName ?? initial.displayName,
                            handle: data.handle ?? initial.handle,
                            photoURL: data.photoURL ?? initial.photoURL,
                            photoFile: null,
                            bio: data.bio ?? initial.bio ?? "",
                        });

                        setAvailability({ checking: false, available: null });
                        return;
                    }
                } catch (err) {
                    console.warn("Failed to read latest user doc on modal open:", err);
                    // fallback to initial props below
                }
            }

            // fallback to initial values
            setForm({
                displayName: initial.displayName,
                handle: initial.handle,
                photoURL: initial.photoURL,
                photoFile: null,
                bio: initial.bio || "",
            });
            setAvailability({ checking: false, available: null });
        };

        initForm();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initial.displayName, initial.handle, initial.photoURL, initial.uid, initial.bio]);

    // run handle availability check when handle changes
    useEffect(() => {
        const raw = (form.handle || "").trim();
        if (!raw) {
            setAvailability({ checking: false, available: null });
            return;
        }
        if (typeof runCheckRef.current === "function") {
            runCheckRef.current(raw);
        }
    }, [form.handle]);

    if (!open) return null;

    const photoURL = form.photoURL || initial.photoURL;
    const email = initial.email || "No email";
    const role = initial.role;
    const uid = initial.uid;

    const shownHandle = (form.handle || initial.handle) ? ensureAt(form.handle || initial.handle) : "—";
    const shownDisplayName = form.displayName || initial.displayName || "No name";
    const shownBio = form.bio ?? initial.bio ?? "";

    const uidFromProfile = profile?.uid || profile?.id || profile?.docId || initial.uid || null;
    const authUid = currentUser?.uid || null;
    const isOwner = Boolean(authUid && uidFromProfile && authUid === uidFromProfile);

    const MAX_BIO = 160;

    const validateLocal = () => {
        setError("");
        const name = (form.displayName || "").trim();
        const rawHandle = (form.handle || "").trim();
        const bio = (form.bio || "").trim();

        if (!name) {
            setError("Display name cannot be empty.");
            return false;
        }

        if (containsProfanity(name)) {
            setError("Display name contains inappropriate words.");
            return false;
        }

        if (containsProfanity(rawHandle)) {
            setError("Handle contains inappropriate words.");
            return false;
        }

        if (containsProfanity(bio)) {
            setError("Description contains inappropriate words.");
            return false;
        }

        if (bio.length > MAX_BIO) {
            setError(`Description is too long (max ${MAX_BIO} characters).`);
            return false;
        }

        const normalized = normalizeHandle(rawHandle);
        if (!normalized) {
            setError("Handle is in an invalid format.");
            return false;
        }

        if (availability.available === false) {
            setError("Handle is already taken.");
            return false;
        }

        if (availability.checking) {
            setError("Checking handle availability, please wait.");
            return false;
        }

        return true;
    };

    // Upload file to Firebase Storage and return { downloadURL, path }
    const uploadImageAndGetURL = (file, uid) => {
        return new Promise((resolve, reject) => {
            if (!file) return resolve({ downloadURL: null, path: null });

            const timestamp = Date.now();
            const safeName = file.name.replace(/\s+/g, "-");
            const path = `${uid}/avatars/${timestamp}-${safeName}`;
            const ref = storageRef(storage, path);
            const uploadTask = uploadBytesResumable(ref, file);

            uploadTask.on(
                "state_changed",
                (snapshot) => {
                    const percent = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                    setUploadProgress(percent);
                },
                (err) => {
                    console.error("upload failed", err);
                    setUploadProgress(null);
                    reject(err);
                },
                async () => {
                    try {
                        const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
                        setUploadProgress(null);
                        resolve({ downloadURL, path });
                    } catch (err) {
                        setUploadProgress(null);
                        reject(err);
                    }
                }
            );
        });
    };

    // Delete object at storagePath (accepts storage path, downloadURL or gs:// url)
    const deleteStoragePath = async (pathOrUrl) => {
        if (!pathOrUrl) return;
        // normalize to storage path like "uid/avatars/..."
        const normalizedPath = extractStoragePathFromUrl(pathOrUrl);
        if (!normalizedPath) {
            console.warn("Could not normalize storage path for deletion:", pathOrUrl);
            return;
        }
        try {
            const ref = storageRef(storage, normalizedPath);
            await deleteObject(ref);
            console.log("Deleted old avatar at", normalizedPath);
        } catch (err) {
            // don't fail whole flow if delete fails; just log
            console.warn("Failed to delete old avatar at", pathOrUrl, err);
        }
    };


    // Save handler - uploads avatar if present, persists photoURL & photoPath & bio to Firestore users/{uid}, deletes old photoPath
    const handleSave = async () => {
        if (!validateLocal()) return;
        if (!isOwner) {
            setError("You must sign in as the owner to edit this profile.");
            return;
        }

        setSaving(true);
        setError("");

        // We'll try to get the user's existing photoPath upfront so we can delete it later
        let existingPhotoPath = null;
        try {
            if (uid) {
                const userDocRef = doc(db, "users", uid);
                const snap = await getDoc(userDocRef);
                if (snap.exists()) {
                    const data = snap.data() || {};
                    // prefer explicit photoPath, fallback to deriving from photoURL
                    existingPhotoPath = data.photoPath || extractStoragePathFromUrl(data.photoURL) || null;
                }
            }
        } catch (err) {
            console.warn("Could not read existing user photoPath; continuing.", err);
        }


        try {
            let finalPhotoURL = form.photoURL || initial.photoURL || "";
            let finalPhotoPath = null;

            // if a new file was selected, upload it
            if (form.photoFile) {
                if (!uid) throw new Error("Cannot upload avatar: missing user id (uid).");
                const { downloadURL, path } = await uploadImageAndGetURL(form.photoFile, uid);
                finalPhotoURL = downloadURL;
                finalPhotoPath = path;
                console.log("Uploaded avatar:", downloadURL, path);
            }

            const normalizedHandle = normalizeHandle(form.handle);
            const updatedProfile = {
                ...profile,
                displayName: form.displayName.trim(),
                handle: normalizedHandle,
                photoURL: finalPhotoURL,
                photoPath: finalPhotoPath || null, // may be null if no new upload
                bio: (form.bio || "").trim(),
                updatedAt: new Date().toISOString(),
                uid: uid,
            };

            // Call parent's onSave if provided (best-effort)
            if (typeof onSave === "function") {
                try {
                    await onSave(updatedProfile);
                } catch (parentErr) {
                    console.warn("onSave threw, continuing local persistence:", parentErr);
                }
            }

            // Persist to Firestore users/{uid} (merge) - include photoPath and bio if available
            if (!uid) throw new Error("Missing uid to persist profile.");
            const userDocRef = doc(db, "users", uid);

            try {
                const payload = {
                    displayName: updatedProfile.displayName,
                    handle: updatedProfile.handle,
                    updatedAt: serverTimestamp(),
                    bio: updatedProfile.bio || "",
                };
                if (finalPhotoURL) payload.photoURL = finalPhotoURL;
                if (finalPhotoPath) payload.photoPath = finalPhotoPath;

                await setDoc(userDocRef, payload, { merge: true });
                console.log(`users/${uid} updated with photoURL/photoPath/bio.`);
            } catch (fireErr) {
                console.error("Failed to write user doc:", fireErr);
                throw fireErr;
            }

            // Update Firebase Auth profile if current user matches
            try {
                const authUser = auth?.currentUser || currentUser || null;
                if (authUser && authUser.uid === uid) {
                    await updateProfile(authUser, {
                        displayName: updatedProfile.displayName,
                        photoURL: updatedProfile.photoURL || null,
                    });
                    if (auth?.currentUser) {
                        auth.currentUser.displayName = updatedProfile.displayName || null;
                        auth.currentUser.photoURL = updatedProfile.photoURL || null;
                    }
                    console.log("Auth profile updated.");
                } else {
                    console.warn("Auth currentUser missing or mismatched; skipping updateProfile.");
                }
            } catch (authErr) {
                console.error("Failed to update Firebase Auth profile:", authErr);
                setError((prev) => (prev ? prev + " (Auth update failed)" : "Profile saved but failed to update Auth profile."));
            }

            // After successful Firestore write, delete old storage file (if there was an old path and it's different from new)
            if (existingPhotoPath && finalPhotoPath && existingPhotoPath !== finalPhotoPath) {
                await deleteStoragePath(existingPhotoPath);
            }

            // Re-read Firestore user doc to get canonical stored values
            let freshData = null;
            try {
                const snap = await getDoc(userDocRef);
                if (snap.exists()) {
                    freshData = snap.data();
                } else {
                    freshData = {
                        displayName: updatedProfile.displayName,
                        handle: updatedProfile.handle,
                        photoURL: updatedProfile.photoURL,
                        photoPath: updatedProfile.photoPath,
                        bio: updatedProfile.bio,
                        uid,
                    };
                }
            } catch (readErr) {
                console.warn("Failed to re-read user doc after write:", readErr);
                freshData = {
                    displayName: updatedProfile.displayName,
                    handle: updatedProfile.handle,
                    photoURL: updatedProfile.photoURL,
                    photoPath: updatedProfile.photoPath,
                    bio: updatedProfile.bio,
                    uid,
                };
            }

            // Cache-bust the local UI src so browser fetches new image immediately
            const canonicalPhotoURL = freshData.photoURL || "";
            const cacheBusted = canonicalPhotoURL ? `${canonicalPhotoURL}?v=${Date.now()}` : canonicalPhotoURL;

            // Update local modal state to show new avatar immediately
            setForm({
                displayName: freshData.displayName || updatedProfile.displayName,
                handle: freshData.handle || updatedProfile.handle,
                photoURL: cacheBusted,
                photoFile: null,
                bio: freshData.bio || updatedProfile.bio || "",
            });

            setAvailability({ checking: false, available: true });
            setEditing(false);

            // Dispatch a canonical profile (without cache-bust) so listeners get clean URL & path
            const dispatchedProfile = {
                ...(freshData || {}),
                uid,
            };
            window.dispatchEvent(new CustomEvent("jift:profileUpdated", { detail: dispatchedProfile }));

            console.log("Profile persisted, old file deleted if needed, and modal updated.");
        } catch (err) {
            console.error("Save failed:", err);
            setError(err?.message || "Failed to save profile.");
        } finally {
            setSaving(false);
            setUploadProgress(null);
        }
    };

    // file change handler
    const onFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) return;

        // basic validation
        if (!file.type.startsWith("image/")) {
            setError("Please select an image file.");
            return;
        }
        const maxBytes = 5 * 1024 * 1024;
        if (file.size > maxBytes) {
            setError("Image is too large (max 5MB).");
            return;
        }

        const reader = new FileReader();
        reader.onload = (ev) => {
            setForm((s) => ({ ...s, photoURL: ev.target.result, photoFile: file }));
            setError("");
        };
        reader.readAsDataURL(file);
    };

    const triggerFilePicker = () => {
        if (!isOwner) return;
        if (fileInputRef.current) fileInputRef.current.click();
    };

    // Convert a Firebase downloadURL (or gs:// URL) into the storage path used by storageRef()
    // Examples:
    //  - "https://firebasestorage.googleapis.com/v0/b/<bucket>/o/uid%2Favatars%2F159...-img.png?alt=media&token=..." => "uid/avatars/159...-img.png"
    //  - "gs://<bucket>/uid/avatars/159...-img.png" => "uid/avatars/159...-img.png"
    //  - already a plain path "uid/avatars/159...-img.png" => returned as-is
    const extractStoragePathFromUrl = (maybeUrl) => {
        if (!maybeUrl) return null;
        // if it already looks like a plain path, just return it
        if (!maybeUrl.includes("://") && !maybeUrl.includes("/o/")) {
            // simple heuristic: no protocol and no '/o/' => treat as storage path
            return maybeUrl;
        }

        try {
            // handle gs://bucket/path
            if (maybeUrl.startsWith("gs://")) {
                // gs://bucket/part1/part2 -> remove bucket
                const parts = maybeUrl.split("/");
                // parts = ["gs:", "", "bucket", "part1", ...]
                return parts.slice(3).join("/");
            }

            // handle https download URL: find '/o/<encoded-path>'
            const u = new URL(maybeUrl);
            const path = u.pathname || "";
            const idx = path.indexOf("/o/");
            if (idx >= 0) {
                // part after /o/
                const encoded = path.slice(idx + 3); // after '/o/'
                const decoded = decodeURIComponent(encoded.split("?")[0]);
                return decoded;
            }

            // fallback: try to parse query param 'name=' or last segment
            const nameParam = u.searchParams.get("name");
            if (nameParam) return decodeURIComponent(nameParam);

            // otherwise return null - can't extract
            return null;
        } catch (e) {
            // not a valid URL; give up
            return null;
        }
    };

    return (
        <Modal onClose={onClose} title="Your profile">
            <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 340 }}>
                <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                    <div
                        style={{
                            position: "relative",
                            width: 72,
                            height: 72,
                            borderRadius: 12,
                            overflow: "hidden",
                            cursor: isOwner && editing ? "pointer" : "default",
                        }}
                        onClick={() => {
                            if (isOwner && editing) triggerFilePicker();
                        }}
                        aria-hidden={!(isOwner && editing)}
                    >
                        {photoURL ? (
                            <img
                                src={photoURL}
                                alt="avatar"
                                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                            />
                        ) : (
                            <div style={{ width: "100%", height: "100%", background: "var(--btn-muted-bg)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28 }}>

                                {shownDisplayName ? shownDisplayName.charAt(0).toUpperCase() : "U"}
                            </div>
                        )}

                        {isOwner && editing && (
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "rgba(0,0,0,0.35)",
                                    color: "#fff",
                                    fontSize: 13,
                                }}
                            >
                                Edit
                            </div>
                        )}
                    </div>

                    <div style={{ display: "flex", flexDirection: "column" }}>
                        <div style={{ fontWeight: 700, fontSize: 18 }}>{shownDisplayName}</div>
                        <div style={{ color: "var(--muted)", marginTop: 4 }}>{shownHandle}</div>
                        <div style={{ color: "var(--muted)", marginTop: 6, fontSize: 13 }}>{role}</div>

                    </div>
                </div>

                {/* hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={onFileChange}
                />

                <hr style={{ margin: "8px 0", border: "none", borderTop: "1px solid #eee" }} />

                {!editing && (
                    <>
                        <div style={{ display: "grid", gap: 10 }}>
                            <div>
                                <div style={{ fontSize: 12, color: "#666" }}>Email</div>
                                <div style={{ marginTop: 6 }}>{email}</div>
                            </div>

                            <div>
                                <div style={{ fontSize: 12, color: "#666" }}>Handle</div>
                                <div style={{ marginTop: 6 }}>{shownHandle}</div>
                            </div>

                            <div>
                                <div style={{ fontSize: 12, color: "#666" }}>About</div>
                                <div style={{ marginTop: 6, whiteSpace: "pre-wrap", color: shownBio ? "var(--text)" : "var(--muted)" }}>{shownBio || "No description"}</div>
                            </div>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                            <button type="button" className="theme-btn" onClick={() => setEditing(true)}>Edit profile</button>
                            <button type="button" className="theme-btn primary" onClick={onClose}>Close</button>
                        </div>
                    </>
                )}

                {editing && (
                    <>
                        <div style={{ display: "grid", gap: 10 }}>
                            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <span style={{ fontSize: 12, color: "#666" }}>Display name</span>
                                <input value={form.displayName} onChange={(e) => setForm(s => ({ ...s, displayName: e.target.value }))} placeholder="Your name" type="text" />
                            </label>

                            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <span style={{ fontSize: 12, color: "#666" }}>Handle</span>
                                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                    <span style={{ color: "#666" }}>@</span>
                                    <input value={form.handle} onChange={(e) => setForm(s => ({ ...s, handle: e.target.value }))} placeholder="your-handle" type="text" />
                                    <div style={{ marginLeft: 6, minWidth: 110 }}>
                                        {availability.checking && <span style={{ fontSize: 12, color: "#888" }}>Checking…</span>}
                                        {availability.available === true && <span style={{ fontSize: 12, color: "green" }}>Available</span>}
                                        {availability.available === false && <span style={{ fontSize: 12, color: "#c52828" }}>Taken</span>}
                                    </div>
                                </div>
                            </label>

                            <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <span style={{ fontSize: 12, color: "#666" }}>About (short)</span>
                                <textarea
                                    value={form.bio}
                                    onChange={(e) => setForm(s => ({ ...s, bio: e.target.value }))}
                                    placeholder="A short description about you (max 160 characters)"
                                    rows={3}
                                    maxLength={MAX_BIO}
                                    style={{ resize: "vertical" }}
                                />
                                <div style={{ fontSize: 12, color: "#666", textAlign: "right" }}>{(form.bio || "").length}/{MAX_BIO}</div>
                            </label>

                            <div>
                                <div style={{ fontSize: 12, color: "#666" }}>Email (read-only)</div>
                                <div style={{ marginTop: 6 }}>{email}</div>
                            </div>

                            {uploadProgress !== null && (
                                <div style={{ fontSize: 13 }}>
                                    Uploading avatar: {uploadProgress}% complete
                                </div>
                            )}

                            {error && <div style={{ color: "var(--danger, #c52828)", fontSize: 13 }}>{error}</div>}
                        </div>

                        {!isOwner && (
                            <div style={{ color: "#c52828", fontSize: 13 }}>
                                You are not signed in as this user — sign in with the account that owns this profile to edit.
                            </div>
                        )}

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                            <button type="button" className="theme-btn" onClick={() => { setEditing(false); setError(""); }} disabled={saving}>Cancel</button>

                            <button
                                type="button"
                                className="theme-btn primary"
                                onClick={handleSave}
                                disabled={saving || availability.available === false || availability.checking || !isOwner}
                            >
                                {saving ? "Saving…" : "Save"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
}

MeModal.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    profile: PropTypes.object,
    currentUser: PropTypes.object,
    onSave: PropTypes.func,
};

MeModal.defaultProps = {
    open: false,
    profile: {},
    currentUser: {},
    onSave: null,
};
